import React, { useState, useEffect, useMemo } from 'react';
import { X, FileSpreadsheet, Check } from 'lucide-react';
import { MatrixData, exportMatricesToExcel } from '../utils/excelMatrixExport';

interface ExcelMatrixExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  matrices: MatrixData[];
  clientName?: string;
}

const ExcelMatrixExportModal: React.FC<ExcelMatrixExportModalProps> = ({
  isOpen,
  onClose,
  matrices,
  clientName
}) => {
  const [selectedMatrices, setSelectedMatrices] = useState<Record<string, boolean>>({});

  // Initialize all matrices as selected when modal opens
  useEffect(() => {
    if (isOpen && matrices.length > 0) {
      const initialSelection: Record<string, boolean> = {};
      matrices.forEach((matrix, index) => {
        initialSelection[`matrix-${index}`] = true;
      });
      setSelectedMatrices(initialSelection);
    }
  }, [isOpen, matrices]);

  const selectedCount = useMemo(() => {
    return Object.values(selectedMatrices).filter(Boolean).length;
  }, [selectedMatrices]);

  const toggleMatrix = (matrixKey: string) => {
    setSelectedMatrices(prev => ({
      ...prev,
      [matrixKey]: !prev[matrixKey]
    }));
  };

  const toggleAll = (selected: boolean) => {
    const newSelection: Record<string, boolean> = {};
    matrices.forEach((_, index) => {
      newSelection[`matrix-${index}`] = selected;
    });
    setSelectedMatrices(newSelection);
  };

  const handleExport = async () => {
    const selectedMatrixData = matrices.filter((_, index) =>
      selectedMatrices[`matrix-${index}`]
    );

    if (selectedMatrixData.length === 0) {
      alert('Please select at least one matrix to export.');
      return;
    }

    const filename = clientName
      ? `${clientName.replace(/[^a-zA-Z0-9]/g, '_')}_matrices.xlsx`
      : 'matrices_export.xlsx';

    try {
      await exportMatricesToExcel(selectedMatrixData, filename, {
        clientName,
        generatedAt: new Date(),
      });
    } catch (err) {
      console.error('Excel export failed:', err);
      alert('Failed to export Excel file. Please try again.');
      return;
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="absolute inset-4 md:inset-8 lg:inset-12 bg-white dark:bg-gray-800 rounded-xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <div className="flex items-center gap-3">
            <FileSpreadsheet className="w-6 h-6 text-green-600" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Export Matrices to Excel
            </h2>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {selectedCount} of {matrices.length} selected
            </span>
            <button
              onClick={handleExport}
              disabled={selectedCount === 0}
              className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <FileSpreadsheet className="w-4 h-4" />
              Export to Excel
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar with matrix checkboxes */}
          <div className="w-80 border-r border-gray-200 dark:border-gray-700 overflow-y-auto bg-white dark:bg-gray-800">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Matrices ({selectedCount}/{matrices.length})
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => toggleAll(true)}
                    className="text-xs px-2 py-1 text-[#1993e5] hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition-colors"
                  >
                    All
                  </button>
                  <button
                    onClick={() => toggleAll(false)}
                    className="text-xs px-2 py-1 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                  >
                    None
                  </button>
                </div>
              </div>

              {matrices.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                  No matrices found in this report.
                </p>
              ) : (
                <div className="space-y-2">
                  {matrices.map((matrix, index) => {
                    const matrixKey = `matrix-${index}`;
                    const isSelected = selectedMatrices[matrixKey] || false;

                    return (
                      <label
                        key={matrixKey}
                        className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                          isSelected
                            ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-700 border border-transparent'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleMatrix(matrixKey)}
                          className="mt-1 h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                        />
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium text-gray-900 dark:text-white block truncate">
                            {matrix.title}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {matrix.headers.length} columns, {matrix.rows.length} rows
                          </span>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}

              {/* Info box */}
              <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Export Info
                </h4>
                <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                  <li>• Each matrix is a separate, titled sheet</li>
                  <li>• Tables are boxed with a colored header row</li>
                  <li>• Citations are automatically removed</li>
                  <li>• Print-ready: landscape, fit-to-width, repeating header</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Preview area */}
          <div className="flex-1 overflow-y-auto p-6 bg-gray-50 dark:bg-gray-900">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Preview
            </h3>

            {selectedCount === 0 ? (
              <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
                <p>Select matrices from the sidebar to preview</p>
              </div>
            ) : (
              <div className="space-y-6">
                {matrices.map((matrix, index) => {
                  const matrixKey = `matrix-${index}`;
                  if (!selectedMatrices[matrixKey]) return null;

                  return (
                    <div
                      key={matrixKey}
                      className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
                    >
                      <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-600" />
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {matrix.title}
                        </h4>
                        <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto">
                          Sheet: {matrix.title.substring(0, 31)}
                        </span>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-gray-100 dark:bg-gray-700">
                              {matrix.headers.map((header, hIndex) => (
                                <th
                                  key={hIndex}
                                  className="px-4 py-2 text-left font-medium text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-600"
                                >
                                  {header}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {matrix.rows.slice(0, 5).map((row, rIndex) => {
                              // Normalize row to {column: value} dict matching the headers.
                              // Three shapes possible:
                              //   1. array            [cell, cell, ...]
                              //   2. plain object     { "Header": cell, ... }
                              //   3. cells-array     { cells: [{column,value,citations}] }  (yoy_renewal add-ons + policy_comparison)
                              // Same normalization as in excelMatrixExport.ts → if these
                              // diverge the preview lies about what'll end up in the xlsx.
                              let rowDict: Record<string, any> = {};
                              let cellsForArray: any[] | null = null;
                              if (Array.isArray(row)) {
                                cellsForArray = row;
                              } else if (row && typeof row === 'object' && Array.isArray((row as any).cells)) {
                                for (const c of (row as any).cells) {
                                  if (c && typeof c === 'object' && typeof c.column === 'string') {
                                    rowDict[c.column] = c.value ?? '';
                                  }
                                }
                              } else if (row && typeof row === 'object') {
                                rowDict = row as Record<string, any>;
                              }
                              const cells = cellsForArray ?? matrix.headers.map(h => rowDict[h] ?? '');
                              return (
                                <tr
                                  key={rIndex}
                                  className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                                >
                                  {cells.map((cell: any, cIndex: number) => (
                                    <td
                                      key={cIndex}
                                      className="px-4 py-2 text-gray-700 dark:text-gray-300"
                                    >
                                      {typeof cell === 'object' && cell !== null
                                        ? (cell.value ?? cell.text ?? cell.content?.text ?? cell.content ?? '')
                                        : String(cell ?? '')}
                                    </td>
                                  ))}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        {matrix.rows.length > 5 && (
                          <div className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
                            ... and {matrix.rows.length - 5} more rows
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExcelMatrixExportModal;
